import React, {useEffect, useRef, useState} from 'react'

import {returnRefundMethods} from '../../../../utils/variables.js'
import {Button, PdfModal} from '../../../components/UI/index.js'
import {CARRIER_LOGOS, TEXT, InfoSection, StatusBanner, StepTitle, carrierDisplayName, formatCosts} from './ui.js'

const RELOAD_INTERVAL_MS = 2500

// Shows an existing (or just created) return parcel. The server decides the label status:
// - available:       label can be printed
// - awaitingPayment: the shipping costs still have to be paid via Mollie
// - pending:         another request is printing the label, reload until it is there
// - failed:          printing failed, the consumer can retry (reloading prints again)
export default function ReturnParcelOverview({portal, color, returnParcel, onReload, onPay}) {
    const [paying, setPaying] = useState(false)
    const [payError, setPayError] = useState('')
    const [retrying, setRetrying] = useState(false)
    const pdfModal = useRef(null)

    const {label, payment} = returnParcel
    const paymentProcessing = label.status === 'awaitingPayment' && ['pending', 'authorized'].includes(payment.status)

    // Keep checking while the label is being created or the payment is being processed
    useEffect(() => {
        if (label.status === 'pending' || paymentProcessing) {
            const timeout = setTimeout(onReload, RELOAD_INTERVAL_MS)
            return () => clearTimeout(timeout)
        }
    }, [returnParcel])

    const retry = async () => {
        setRetrying(true)
        await onReload()
        setRetrying(false)
    }

    const pay = async () => {
        setPaying(true)
        setPayError('')

        try {
            await onPay()
        } catch (err) {
            setPayError(err.message)
            setPaying(false)
        }
    }

    return (
        <div className='rp-fade-in'>
            <StepTitle
                title='Je retour is aangemeld'
                subtitle={returnParcel.trackTrace ? `Track & Trace: ${returnParcel.trackTrace}` : undefined}
            />

            {label.status === 'available' &&
                <>
                    <StatusBanner tone='success' title='Je retourlabel staat klaar'>
                        Print het label, plak het op je pakket en lever het in bij een {carrierDisplayName(returnParcel.carrier)} servicepunt.
                        {portal.sendRetourConfirmationEmail && returnParcel.address.email && <> Het label is ook gemaild naar <b>{returnParcel.address.email}</b>.</>}
                    </StatusBanner>

                    <Button
                        variant='solid'
                        color={color}
                        style={{width: '100%', marginBottom: 8}}
                        icon='mdi mdi-printer'
                        label=' Print retourlabel'
                        onClick={() => pdfModal.current.open(label.file)}
                    />
                </>
            }

            {label.status === 'awaitingPayment' && paymentProcessing &&
                <StatusBanner tone='loading' title='Je betaling wordt verwerkt'>
                    Zodra de betaling is ontvangen verschijnt hier je retourlabel.
                </StatusBanner>
            }

            {label.status === 'awaitingPayment' && !paymentProcessing &&
                <>
                    {['failed', 'canceled', 'expired'].includes(payment.status) ?
                        <StatusBanner tone='warning' title='De betaling is niet gelukt'>
                            Probeer het opnieuw om je retourlabel te ontvangen.
                        </StatusBanner> :
                        <StatusBanner tone='info' title='Betaal de verzendkosten'>
                            Na betaling van {formatCosts(returnParcel.shippingCosts)} kun je direct je retourlabel printen.
                        </StatusBanner>
                    }

                    {payError && <StatusBanner tone='error'>{payError}</StatusBanner>}

                    <Button
                        variant='solid'
                        color={color}
                        style={{width: '100%', marginBottom: 8}}
                        loading={paying}
                        icon='mdi mdi-credit-card-outline'
                        label={` Betaal ${formatCosts(returnParcel.shippingCosts)}`}
                        onClick={pay}
                    />
                </>
            }

            {label.status === 'pending' &&
                <StatusBanner tone='loading' title='Je retourlabel wordt aangemaakt'>
                    Een ogenblik geduld, dit duurt meestal maar een paar seconden.
                </StatusBanner>
            }

            {label.status === 'failed' &&
                <>
                    <StatusBanner tone='error' title='Het retourlabel kon niet worden aangemaakt'>
                        Je retour is wel aangemeld. Probeer het over een paar minuten opnieuw of neem contact op met {portal.customerName}.
                    </StatusBanner>

                    <Button
                        variant='outline'
                        color={color}
                        style={{width: '100%', marginBottom: 8}}
                        icon='mdi mdi-refresh'
                        label=' Opnieuw proberen'
                        loading={retrying}
                        onClick={retry}
                    />
                </>
            }

            <div style={{marginTop: 16}}>
                <InfoSection title='Verzending'>
                    <div style={{display: 'flex', alignItems: 'center', gap: 12}}>
                        {CARRIER_LOGOS[carrierDisplayName(returnParcel.carrier)] &&
                            <img src={CARRIER_LOGOS[carrierDisplayName(returnParcel.carrier)]} alt='' style={{height: 28, maxWidth: 56, objectFit: 'contain'}} />
                        }
                        <div style={{flex: 1}}>{carrierDisplayName(returnParcel.carrier)} retourzending</div>
                        <div style={{fontWeight: 500}}>{formatCosts(returnParcel.shippingCosts)}</div>
                    </div>
                </InfoSection>

                {!!returnParcel.items.length &&
                    <InfoSection title='Items'>
                        {returnParcel.items.map((item, index) => (
                            <div key={index} style={{display: 'flex', gap: 12, padding: '2px 0'}}>
                                <div style={{flex: 1}}>
                                    {item.description}
                                    {item.reason && <div style={{fontSize: 13, color: TEXT.muted}}>{item.reason}</div>}
                                </div>
                                <div style={{color: TEXT.muted}}>{item.quantity}×</div>
                            </div>
                        ))}
                    </InfoSection>
                }

                {returnParcel.refundMethod && returnRefundMethods[returnParcel.refundMethod] &&
                    <InfoSection title='Terugbetaling'>
                        {returnRefundMethods[returnParcel.refundMethod].title}
                        {returnParcel.refundMethod === 'exchange' && returnParcel.refundMethodComment &&
                            <div style={{fontSize: 13, color: TEXT.muted}}>{returnParcel.refundMethodComment}</div>
                        }
                    </InfoSection>
                }

                <InfoSection title='Jouw gegevens'>
                    <AddressLines address={returnParcel.address} />
                </InfoSection>
            </div>

            <PdfModal ref={pdfModal} />
        </div>
    )
}

export function AddressLines({address}) {
    return (
        <>
            <div>{address.name}</div>
            <div>{`${address.street} ${address.nr}${address.addition || ''}`}</div>
            <div>{`${address.postalCode} ${address.city}`}</div>
            {address.email && <div style={{color: TEXT.muted}}>{address.email}</div>}
            {address.phone && <div style={{color: TEXT.muted}}>{address.phone}</div>}
        </>
    )
}
